import { useCallback } from 'react';
import { Node, NodeChange, applyNodeChanges } from '@xyflow/react';
import { GRID_CONFIG } from '../gridConfig';
import { ResultCircleNodeData } from '../components/ResultCircleNode';
import { DraggableResultCircleNodeData } from '../components/DraggableResultCircleNode';
import { RelativesMapData } from '../components/Map3Relatives';
import { getLocalXForColumn } from '../utils/layoutMath';

/**
 * Custom hook for handling node movement and position updates in React Flow dependency maps
 *
 * This hook manages draggable result nodes with sophisticated movement constraints and
 * automatic deadline updates. It's the most complex hook in the system, handling:
 *
 * **Supported Node Types:**
 * - `'draggableResultCircleNode'` - Interactive result nodes that can be repositioned
 *
 * **Movement Constraints:**
 * - **Horizontal Only**: Nodes can only move along the X axis within their row
 * - **Y Lock**: Y position is locked to maintain proper row alignment
 * - **Boundary Constraints**: X position constrained between first and last status cells
 * - **Grid Snapping**: On drag end, nodes snap to the nearest column center
 * - **Parent Coordinates**: All positioning is relative to parent MapNode (extent: 'parent')
 *
 * **Grid Boundaries:**
 * - **Left Boundary**: Right edge of objectives column (GRID_CONFIG.objectiveWidth)
 * - **Right Boundary**: Right edge of last status column for the map
 * - **Map-Specific**: Each map's nodes are constrained to their map's column range
 *
 * **Deadline Integration:**
 * - **Automatic Updates**: Updates result deadlines based on final column position
 * - **Interval Mapping**: Maps dropped position to corresponding timeline interval
 * - **Queue Integration**: Uses parent callback for throttled Redux dispatch
 *
 * **View Mode Support:**
 * - **Stacked**: Uses defaultIntervals for deadline calculation
 * - **SharedTimeline**: Uses adjustedIntervals for unified timeline positioning
 *
 * **Architecture Notes:**
 * - Nodes are children of MapNodes with `extent: 'parent'` constraint
 * - All coordinates are relative to parent MapNode, not global
 * - Boundary calculations account for React Flow sub-flow coordinate system
 * - Integrates with throttled dispatch system to prevent runaway Redux updates
 *
 * **Performance Considerations:**
 * - During drag: Only applies position constraints, no deadline updates
 * - On drag end: Applies grid snapping + deadline updates
 * - Preserves positions for non-draggable node types (performance optimization)
 *
 * @param relativesMapsData - Map data containing layout and interval information
 * @param setNodes - React state setter for updating node positions
 * @param viewMode - Current view mode ('stacked' or 'sharedTimeline')
 * @param onUpdateResultDeadline - Callback to queue deadline updates for throttled dispatch
 * @returns Callback function to handle NodeChange events from React Flow
 *
 * @example
 * ```typescript
 * const onNodesChange = useNodesChangeHandler(
 *   relativesMapsData,
 *   setNodes,
 *   'stacked',
 *   queueResultDeadlineUpdate
 * );
 *
 * // In React Flow:
 * <ReactFlow onNodesChange={onNodesChange} ... />
 * ```
 */
export function useNodesChangeHandler(
  relativesMapsData: RelativesMapData[],
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>,
  viewMode: 'stacked' | 'sharedTimeline' = 'stacked',
  onUpdateResultDeadline: (resultUuid: string, newDeadline: string) => void,
) {
  return useCallback(
    (changes: NodeChange[]) => {
      setNodes((nds) => {
        const nextNodes = changes.reduce<Node[]>((acc, change) => {
          // Handle position changes (both during drag and on drag end)
          if (change.type === 'position' && change.position) {
            const node = nds.find((n) => n.id === change.id);
            if (!node) return acc;

            // Only handle position changes for draggable result nodes
            // Skip position changes for map nodes and other non-draggable node types (e.g. ResultCircleNode)
            if (node.type !== 'draggableResultCircleNode') {
              return acc;
            }

            // Find which map this node belongs to by matching the node's objectiveUuid
            // This is critical for determining the correct offsets and boundaries for the node
            const nodeMap = relativesMapsData.find((relative) =>
              relative.objectives.some(
                (o) =>
                  o.uuid ===
                  (node.data as ResultCircleNodeData | DraggableResultCircleNodeData).objectiveUuid,
              ),
            );
            if (!nodeMap) return acc;

            // Keep original Y position (constrain to row)
            const originalY = node.position.y;

            // --- BOUNDARY CALCULATIONS FOR REACT FLOW SUB FLOWS ---
            // Note: Result nodes and circle nodes are children of MapNodes with extent: 'parent',
            // so their coordinates are relative to the parent MapNode, not global coordinates.
            // The left boundary is the right edge of the objectives column within the parent.
            const minX = GRID_CONFIG.objectiveWidth;

            // The right boundary is the right edge of the last status column within the parent.
            const statusColumns = nodeMap.columnsInfo.filter(
              (col) => typeof col.statusColumnIndex === 'number',
            );
            const lastStatusCol = statusColumns.at(-1);
            // Use localX since we're working within the parent MapNode's coordinate system
            const maxX = lastStatusCol
              ? getLocalXForColumn(lastStatusCol) + lastStatusCol.width
              : minX;

            if (change.dragging) {
              // During drag: constrain X position between map boundaries within the parent
              const constrainedX = Math.max(minX, Math.min(maxX, change.position.x));

              const updatedNodes = applyNodeChanges(
                [
                  {
                    ...change,
                    position: { x: constrainedX, y: originalY },
                  },
                ],
                acc,
              ) as Node[];
              return updatedNodes;
            } else {
              // On drag end: snap to grid within the parent MapNode's coordinate system
              const relativeX = change.position.x - GRID_CONFIG.objectiveWidth;
              const intervalIndex = Math.max(0, Math.round(relativeX / GRID_CONFIG.cellWidth));
              // Calculate the snapped X position (center of the nearest column).
              let snappedX = GRID_CONFIG.objectiveWidth + intervalIndex * GRID_CONFIG.cellWidth;

              // Constrain snapped position to parent boundaries
              snappedX = Math.max(minX, Math.min(maxX, snappedX));

              // Recalculate interval index based on constrained position
              // This is important: if the node was dragged past the right boundary, this ensures
              // the deadline is set for the last column, not a non-existent one.
              const constrainedIntervalIndex = Math.floor(
                (snappedX - GRID_CONFIG.objectiveWidth) / GRID_CONFIG.cellWidth,
              );

              // Get the new interval end date based on the constrained column
              const intervals =
                viewMode === 'stacked' ? nodeMap.defaultIntervals : nodeMap.adjustedIntervals;
              const newInterval = intervals[constrainedIntervalIndex];
              if (newInterval) {
                const nodeData = node.data as ResultCircleNodeData | DraggableResultCircleNodeData;
                const result = nodeData.result;

                // Update the result's deadline/forIntervalEnding
                onUpdateResultDeadline(result.uuid, newInterval.end.toISOString());
              }

              const updatedNodes = applyNodeChanges(
                [
                  {
                    ...change,
                    position: { x: snappedX, y: originalY },
                  },
                ],
                acc,
              ) as Node[];
              return updatedNodes;
            }
          }

          // Apply other node changes as is (selection, removal, etc.)
          // These don't require custom deadline update logic
          const updatedNodes = applyNodeChanges([change], acc) as Node[];
          return updatedNodes;
        }, nds);

        return nextNodes;
      });
    },
    [relativesMapsData, onUpdateResultDeadline, viewMode],
  );
}
